import {
  faArrowsRotate,
  faBusinessTime,
  faClock,
  faClockRotateLeft,
  faLink,
  faLinkSlash,
  faListCheck,
  faNoteSticky,
  faPlay,
  faTrashCan,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import CardSkeleton from 'src/components/maintenance/CardSkeleton';
import BadgeRow from 'src/components/ui/BadgeRow';
import Button from 'src/components/ui/Button';
import EmptyState from 'src/components/ui/EmptyState';
import InfoRow from 'src/components/ui/InfoRow';
import MetaLine from 'src/components/ui/MetaLine';
import ResourceCardAlt from 'src/components/ui/ResourceCardAlt';
import SectionLabel from 'src/components/ui/SectionLabel';
import Skeleton from 'src/components/ui/Skeleton';
import StatusBadge from 'src/components/ui/StatusBadge';
import {
  FULFILLMENT_STATUS,
  MAINTENANCE_EVENT_STATUS,
  MAINTENANCE_EVENT_STATUS_DISPLAY,
  MAINTENANCE_SCOPE,
} from 'src/constants/maintenance';
import {
  useDowntime,
  useEventFulfillments,
  useFulfillmentReportsByFulfillmentUri,
  useLastPerformedByRoot,
  useRequirementWithChecklist,
  useResolvedRequirementsByUris,
} from 'src/hooks/services/useMaintenance';
import { useUsersByUri } from 'src/hooks/services/useUser';
import { useDateLocale } from 'src/hooks/useDateLocale';
import {
  deriveScope,
  getRequirementInstructions,
  getRequirementRootUuid,
  normalizeReportFile,
  parseReportRowValue,
  reportMatchesInstruction,
  summarizeFrequency,
} from 'src/utils/maintenance';

import { DowntimeViewCard } from '../DowntimeCards';
import InstructionAnswer from '../InstructionAnswer';
import RequirementCard from '../RequirementCard';

/** Resolve one fulfillment's requirement (current or pinned/superseded revision). */
const useResolvedRequirement = (fulfillment, requirementByUri) => {
  const inMap = requirementByUri[fulfillment.maintenance_requirement];
  const { data: fetched } = useRequirementWithChecklist(
    fulfillment.maintenance_requirement,
    !inMap
  );
  return inMap || fetched || null;
};

const RequirementPreview = ({
  fulfillment,
  requirementByUri,
  anchor,
  instanceTargetUri,
  lastPerformedByRoot,
  defaultOpen,
  reports,
}) => {
  const requirement = useResolvedRequirement(fulfillment, requirementByUri);
  const { formattedDate } = useDateLocale();
  if (!requirement) return null;
  const lastPerformedAt = lastPerformedByRoot[getRequirementRootUuid(requirement)];
  return (
    <RequirementCard
      requirement={requirement}
      anchor={anchor}
      isInstance={deriveScope(requirement, instanceTargetUri) === MAINTENANCE_SCOPE.INSTANCE}
      lastPerformed={lastPerformedAt ? formattedDate(lastPerformedAt) : null}
      defaultOpen={defaultOpen}
      reports={reports}
    />
  );
};

RequirementPreview.propTypes = {
  fulfillment: PropTypes.object.isRequired,
  requirementByUri: PropTypes.object.isRequired,
  anchor: PropTypes.object.isRequired,
  instanceTargetUri: PropTypes.string.isRequired,
  lastPerformedByRoot: PropTypes.object,
  defaultOpen: PropTypes.bool,
  reports: PropTypes.array,
};

RequirementPreview.defaultProps = { lastPerformedByRoot: {}, defaultOpen: false, reports: [] };

/**
 * Completed-event fulfillment block — iterates the requirement's INSTRUCTIONS
 * (rapidfab parity), so every checklist step shows its captured answer or a
 * muted "None". Header carries the version + cadence; footer carries the
 * done-time and the version-history link.
 */
const FulfillmentReport = ({
  fulfillment,
  requirementByUri,
  reports,
  onViewFile,
  onOpenHistory,
}) => {
  const requirement = useResolvedRequirement(fulfillment, requirementByUri);
  const isSkipped = fulfillment.status === FULFILLMENT_STATUS.SKIPPED;
  const { formattedTime } = useDateLocale();
  const instructions = requirement ? getRequirementInstructions(requirement) : [];
  const name = requirement?.name || 'Requirement';
  const revisionLabel = requirement?.revision_number ? `v${requirement.revision_number}` : null;

  const renderAnswer = instruction => {
    const report = reports.find(item => reportMatchesInstruction(item, instruction));
    const file = normalizeReportFile(report?.report_file || null);
    const value = parseReportRowValue(instruction.reportType, report);
    return (
      <InstructionAnswer
        instruction={instruction}
        value={value}
        file={file}
        onViewFile={onViewFile}
      />
    );
  };

  return (
    <div className='mnt-report'>
      <div className='mnt-report__head'>
        <div className='mnt-report__headmain'>
          <span className='mnt-report__name'>{name}</span>
          {!isSkipped && requirement && (
            <MetaLine icon={faArrowsRotate}>{summarizeFrequency(requirement)}</MetaLine>
          )}
        </div>
        {isSkipped ? (
          <StatusBadge size='sm' uppercase={false} variant='default'>
            Skipped
          </StatusBadge>
        ) : (
          revisionLabel && (
            <StatusBadge size='sm' tone='outline' uppercase={false}>
              {revisionLabel}
            </StatusBadge>
          )
        )}
      </div>

      {isSkipped ? (
        fulfillment.notes && (
          <div className='mnt-report__skip'>Operator note: “{fulfillment.notes}”</div>
        )
      ) : (
        <>
          <div className='mnt-report__rows'>
            {instructions.map(instruction => (
              <div key={instruction.uuid || instruction.uri} className='mnt-report__row'>
                <span className='mnt-report__q'>{instruction.question}</span>
                <span className='mnt-report__a'>{renderAnswer(instruction)}</span>
              </div>
            ))}
            {!instructions.length && (
              <div className='mnt-report__empty'>No checklist steps on this version.</div>
            )}
          </div>
          <div className='mnt-report__foot'>
            <span className='mnt-report__done'>
              {fulfillment.completed_at && (
                <>
                  <FontAwesomeIcon icon={faClock} /> Done {formattedTime(fulfillment.completed_at)}
                </>
              )}
            </span>
            {onOpenHistory && requirement?.root_requirement && (
              <Button
                variant='link'
                size='small'
                aria-label='Version history'
                title='Version history'
                icon={<FontAwesomeIcon icon={faClockRotateLeft} />}
                onClick={() => onOpenHistory(requirement)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

FulfillmentReport.propTypes = {
  fulfillment: PropTypes.object.isRequired,
  requirementByUri: PropTypes.object.isRequired,
  reports: PropTypes.array,
  onViewFile: PropTypes.func.isRequired,
  onOpenHistory: PropTypes.func,
};

FulfillmentReport.defaultProps = { reports: [], onOpenHistory: null };

const DowntimeBlock = ({ downtime, closed }) => (
  <>
    <SectionLabel icon={faBusinessTime}>Downtime</SectionLabel>
    {downtime ? (
      <DowntimeViewCard
        downtime={downtime}
        chip={
          <StatusBadge size='sm' uppercase={false} variant='warning'>
            <FontAwesomeIcon icon={faLink} /> {closed ? 'Closed' : 'Linked'}
          </StatusBadge>
        }
      />
    ) : (
      <EmptyState variant='inline' icon={faLinkSlash}>
        No downtime linked
      </EmptyState>
    )}
  </>
);

DowntimeBlock.propTypes = { downtime: PropTypes.object, closed: PropTypes.bool };
DowntimeBlock.defaultProps = { downtime: null, closed: false };

const SKELETON_CARD_STYLE = {
  border: '1px solid #e1e4e8',
  borderRadius: 12,
  padding: 13,
  marginBottom: 10,
};

const RequirementCardSkeleton = () => <CardSkeleton avatar={40} lines={['55%', '32%']} />;

const RequirementListSkeleton = () => (
  <>
    {Array.from({ length: 3 }).map((_, index) => (
      // eslint-disable-next-line react/no-array-index-key
      <RequirementCardSkeleton key={index} />
    ))}
  </>
);

const DowntimeSkeleton = () => (
  <>
    <SectionLabel icon={faBusinessTime}>Downtime</SectionLabel>
    <div style={SKELETON_CARD_STYLE}>
      <Skeleton width='65%' height={14} />
      <Skeleton width='45%' height={12} style={{ marginTop: 9 }} />
    </div>
  </>
);

const EventDowntimeSection = ({
  isCancelled,
  canLinkDowntime,
  hasDowntime,
  loading,
  downtime,
  isComplete,
}) => {
  if (isCancelled) {
    return (
      <p className='text-muted' style={{ fontSize: 13 }}>
        Cancelled before completion — the selected requirements were auto-skipped.
      </p>
    );
  }
  if (!canLinkDowntime) return null;
  if (hasDowntime && loading) return <DowntimeSkeleton />;
  return <DowntimeBlock downtime={downtime} closed={isComplete} />;
};

EventDowntimeSection.propTypes = {
  isCancelled: PropTypes.bool,
  canLinkDowntime: PropTypes.bool,
  hasDowntime: PropTypes.bool,
  loading: PropTypes.bool,
  downtime: PropTypes.object,
  isComplete: PropTypes.bool,
};

EventDowntimeSection.defaultProps = {
  isCancelled: false,
  canLinkDowntime: false,
  hasDowntime: false,
  loading: false,
  downtime: null,
  isComplete: false,
};

/** Event detail bottom-sheet — content for the ActionPanel. */
const EventDetailSheet = ({
  event,
  anchor,
  instanceTargetUri,
  requirementByUri,
  onStart,
  onResume,
  onViewFile,
  onOpenHistory,
  onChangeDowntime,
  onCancelPlanned,
}) => {
  // Hide the ybug feedback launcher while this sheet is open - it overlaps the
  // sheet's bottom content. Restored on close (other screens keep it).
  React.useEffect(() => {
    document.body.classList.add('mnt-hide-ybug');
    return () => document.body.classList.remove('mnt-hide-ybug');
  }, []);

  const { data: fulfillments = [], isLoading: fulfillmentsLoading } = useEventFulfillments(
    event.uri
  );
  const fulfillmentUris = React.useMemo(
    () =>
      fulfillments
        .map(fulfillment => fulfillment.uri)
        .filter(Boolean)
        .sort(),
    [fulfillments]
  );
  const { data: reportsByFulfillmentUri = {} } =
    useFulfillmentReportsByFulfillmentUri(fulfillmentUris);
  // Pinned/superseded revisions not in the passed map are batch-resolved here
  // (one LIST + deduped checklists); the per-row useRequirementWithChecklist
  // then finds them in-map and stays idle instead of firing a fetch pair each.
  const missingRequirementUris = React.useMemo(
    () =>
      fulfillments
        .map(fulfillment => fulfillment.maintenance_requirement)
        .filter(uri => uri && !requirementByUri[uri]),
    [fulfillments, requirementByUri]
  );
  const { data: resolvedRequirementByUri = {} } =
    useResolvedRequirementsByUris(missingRequirementUris);
  const mergedRequirementByUri = React.useMemo(
    () => ({ ...resolvedRequirementByUri, ...requirementByUri }),
    [resolvedRequirementByUri, requirementByUri]
  );
  const { data: downtime, isLoading: downtimeLoading } = useDowntime(event.downtime);
  const { formattedDateTime } = useDateLocale();
  // Gate the action buttons while requirements/downtime load so nothing acts on
  // half-loaded data (the sheet's own X close stays available).
  const contentLoading = fulfillmentsLoading || (Boolean(event.downtime) && downtimeLoading);
  // Real per-requirement history for the "Last performed" strip — derived from
  // this machine's completed fulfillments, keyed by requirement root.
  const lastPerformedByRoot = useLastPerformedByRoot(
    instanceTargetUri,
    event.status === MAINTENANCE_EVENT_STATUS.PLANNED ||
      event.status === MAINTENANCE_EVENT_STATUS.IN_PROGRESS
  );

  // Actor: who completed/started the event; a planned event shows the
  // downtime's assignee (the person who scheduled the work).
  const actorUri = event.completed_by || event.started_by || downtime?.assigned_user || null;
  const { data: usersByUri = {} } = useUsersByUri(
    actorUri ? [actorUri] : [],
    'maintenance-event-detail-actor'
  );
  const actor = actorUri ? usersByUri[actorUri] : null;
  const actorName = actor?.name || actor?.username || null;

  const status = event.status;
  const display = MAINTENANCE_EVENT_STATUS_DISPLAY[status] || {};
  const isPlanned = status === MAINTENANCE_EVENT_STATUS.PLANNED;
  const isInProgress = status === MAINTENANCE_EVENT_STATUS.IN_PROGRESS;
  const isComplete = status === MAINTENANCE_EVENT_STATUS.COMPLETE;
  const isCancelled = status === MAINTENANCE_EVENT_STATUS.CANCELLED;

  const done = fulfillments.filter(f => f.status === FULFILLMENT_STATUS.COMPLETE).length;

  return (
    <div className='mnt-sheet'>
      <BadgeRow>
        <StatusBadge dot size='sm' uppercase={false} variant={display.variant}>
          {display.label}
        </StatusBadge>
        {actorName && (
          <StatusBadge size='sm' uppercase={false} icon={faUser}>
            by {actorName}
          </StatusBadge>
        )}
        {isComplete && (
          <StatusBadge size='sm' uppercase={false} variant='default'>
            <FontAwesomeIcon icon={faClock} /> {formattedDateTime(event.completed_at)}
          </StatusBadge>
        )}
      </BadgeRow>

      {isInProgress && (
        <ResourceCardAlt title='Progress'>
          <InfoRow icon={faClock} label='Started' value={formattedDateTime(event.started_at)} />
          <InfoRow
            icon={faListCheck}
            label='Requirements'
            value={
              fulfillmentsLoading ? (
                <Skeleton width={72} height={13} />
              ) : (
                `${done} of ${fulfillments.length} done`
              )
            }
          />
        </ResourceCardAlt>
      )}

      <EventDowntimeSection
        isCancelled={isCancelled}
        canLinkDowntime={anchor.canLinkDowntime}
        hasDowntime={Boolean(event.downtime)}
        loading={downtimeLoading}
        downtime={downtime}
        isComplete={isComplete}
      />

      {(isPlanned || isInProgress) && (fulfillmentsLoading || fulfillments.length > 0) && (
        <>
          <SectionLabel icon={faListCheck} hint='tap to preview steps'>
            Requirements
          </SectionLabel>
          {fulfillmentsLoading ? (
            <RequirementListSkeleton />
          ) : (
            fulfillments.map((fulfillment, index) => (
              <RequirementPreview
                key={fulfillment.uri}
                fulfillment={fulfillment}
                requirementByUri={mergedRequirementByUri}
                anchor={anchor}
                instanceTargetUri={instanceTargetUri}
                lastPerformedByRoot={lastPerformedByRoot}
                defaultOpen={index === 0}
                reports={reportsByFulfillmentUri[fulfillment.uri] || []}
              />
            ))
          )}
        </>
      )}

      {isComplete && (fulfillmentsLoading || fulfillments.length > 0) && (
        <>
          <SectionLabel icon={faListCheck}>What was done</SectionLabel>
          {fulfillmentsLoading ? (
            <RequirementListSkeleton />
          ) : (
            fulfillments.map(fulfillment => (
              <FulfillmentReport
                key={fulfillment.uri}
                fulfillment={fulfillment}
                requirementByUri={mergedRequirementByUri}
                reports={reportsByFulfillmentUri[fulfillment.uri] || []}
                onViewFile={onViewFile}
                onOpenHistory={onOpenHistory}
              />
            ))
          )}
        </>
      )}

      {(isPlanned || isInProgress || event.notes) && (
        <>
          <SectionLabel icon={faNoteSticky}>Notes</SectionLabel>
          <div className={`mnt-notes-box${event.notes ? '' : ' mnt-notes-box--empty'}`}>
            {event.notes || 'No notes added.'}
          </div>
        </>
      )}

      {(isPlanned || isInProgress) && (
        <>
          <Button
            fill
            block
            variant='success'
            size='large'
            className='mt-3'
            disabled={contentLoading}
            icon={<FontAwesomeIcon icon={faPlay} />}
            onClick={isPlanned ? onStart : onResume}
          >
            {isPlanned ? 'Start maintenance' : 'Resume work'}
          </Button>
          {anchor.canLinkDowntime && onChangeDowntime && isPlanned && (
            <Button
              block
              variant='primary'
              className='mt-2'
              disabled={contentLoading}
              icon={<FontAwesomeIcon icon={faLink} />}
              onClick={() => onChangeDowntime(event, downtime)}
            >
              {event.downtime ? 'Change downtime' : 'Link downtime'}
            </Button>
          )}
          {isPlanned && onCancelPlanned && (
            <Button
              block
              variant='danger'
              className='mt-2'
              disabled={contentLoading}
              icon={<FontAwesomeIcon icon={faTrashCan} />}
              onClick={() => {
                if (window.confirm('Delete this planned maintenance event?')) {
                  onCancelPlanned(event);
                }
              }}
            >
              Delete planned event
            </Button>
          )}
        </>
      )}
    </div>
  );
};

EventDetailSheet.propTypes = {
  event: PropTypes.object.isRequired,
  anchor: PropTypes.object.isRequired,
  instanceTargetUri: PropTypes.string.isRequired,
  requirementByUri: PropTypes.object.isRequired,
  onStart: PropTypes.func,
  onResume: PropTypes.func,
  onViewFile: PropTypes.func,
  onOpenHistory: PropTypes.func,
  onChangeDowntime: PropTypes.func,
  onCancelPlanned: PropTypes.func,
};

EventDetailSheet.defaultProps = {
  onStart: () => {},
  onResume: () => {},
  onViewFile: () => {},
  onOpenHistory: () => {},
  onChangeDowntime: null,
  onCancelPlanned: null,
};

export default EventDetailSheet;
